// App-lock state machine (Tool Plan §4.14 / §4.15; I-1a Batch 4, IP2-29).
//
//   checking ──(no keyring)──▶ first-run ──(set up)──▶ unlocked
//      │                          │
//      │                          └─(another copy set up first)─▶ locked
//      └──(keyring)──▶ locked ──(try)──▶ unlocking ──(opened)──▶ unlocked
//                        ▲   ▲               │                       │
//                        │   └───(failed)────┘                       │
//                        └──────────────────(lock)───────────────────┘
//   locked ──(deleted everything)──▶ first-run
//
// Hide (S12, I-1a Batch 5): from unlocked or a try in progress → locked (the
// neutral Unlock screen); from first run → checking, so the keyring is read
// again: Unlock if a keyring now exists (set-up finished meanwhile), else the
// first-run start. Recheck: a late result from a screen that Hide or a lock
// already closed (another copy set up, everything deleted) → read the keyring
// again from first run or locked.
//
// Pure: the shell holds the open store; this only says which screen is allowed.
// An event that does not fit the current state is ignored (same state back).

export type LockState =
  | { readonly name: 'checking' }
  | { readonly name: 'first-run' }
  | { readonly name: 'locked' }
  | { readonly name: 'unlocking' }
  /** failedTries = wrong tries since the last unlock, shown once on Home (IR-22). */
  | { readonly name: 'unlocked'; readonly failedTries: number }

export type LockEvent =
  | { readonly type: 'checked'; readonly hasKeyring: boolean }
  | { readonly type: 'set-up' }
  | { readonly type: 'already-set-up' }
  | { readonly type: 'try' }
  | { readonly type: 'failed' }
  | { readonly type: 'opened'; readonly failedTries: number }
  | { readonly type: 'lock' }
  | { readonly type: 'deleted' }
  | { readonly type: 'hide' }
  | { readonly type: 'recheck' }

export const INITIAL_LOCK_STATE: LockState = { name: 'checking' }

export function lockReducer(state: LockState, event: LockEvent): LockState {
  switch (state.name) {
    case 'checking':
      return event.type === 'checked' ? { name: event.hasKeyring ? 'locked' : 'first-run' } : state
    case 'first-run':
      if (event.type === 'set-up') {
        return { name: 'unlocked', failedTries: 0 }
      }
      if (event.type === 'hide' || event.type === 'recheck') {
        return { name: 'checking' }
      }
      return event.type === 'already-set-up' ? { name: 'locked' } : state
    case 'locked':
      if (event.type === 'try') {
        return { name: 'unlocking' }
      }
      if (event.type === 'recheck') {
        return { name: 'checking' }
      }
      return event.type === 'deleted' ? { name: 'first-run' } : state
    case 'unlocking':
      if (event.type === 'opened') {
        return { name: 'unlocked', failedTries: event.failedTries }
      }
      return event.type === 'failed' || event.type === 'lock' || event.type === 'hide' ? { name: 'locked' } : state
    case 'unlocked':
      return event.type === 'lock' || event.type === 'hide' ? { name: 'locked' } : state
  }
}
